import {
  KEY_REQUEST_ACTION,
  KEY_REQUEST_SCOPE,
  KEY_REQUEST_STATUS,
  normalizeMobileNumber
} from '../config/keyRequest';

function requestTime(request) {
  return new Date(request.completedOn || request.createdOn || 0).getTime();
}

/**
 * Reconstruct currently valid keys from completed key requests.
 * Phone "Vis nøkler" uses Unloc GetKeys via APIM instead; this remains for replay/fallback.
 */
export function deriveActiveKeys({
  requests = [],
  locks = [],
  assignments = [],
  persistent = false,
  now = new Date()
}) {
  const locksById = new Map(locks.map((lock) => [lock.lockId, lock]));
  const locksByResident = new Map();

  for (const assignment of assignments) {
    const list = locksByResident.get(assignment.residentId) || [];
    list.push(assignment.lockId);
    locksByResident.set(assignment.residentId, list);
  }

  const unlocLockIds = locks
    .filter((lock) => lock.provider === 'unloc')
    .map((lock) => lock.lockId);

  const keys = new Map();

  const addLocks = (lockIds, request) => {
    const grantedOn = request.completedOn || request.createdOn;
    const grantedAt = grantedOn ? new Date(grantedOn) : null;
    const expiresOn =
      persistent || !request.durationMinutes || !grantedAt
        ? null
        : new Date(grantedAt.getTime() + request.durationMinutes * 60 * 1000);

    for (const lockId of lockIds) {
      if (!lockId) {
        continue;
      }

      keys.set(lockId, {
        lockId,
        name: locksById.get(lockId)?.name || 'Ukjent lås',
        grantedOn,
        expiresOn,
        requestId: request.requestId
      });
    }
  };

  const sorted = [...requests].sort((left, right) => requestTime(left) - requestTime(right));

  for (const request of sorted) {
    if (request.status !== KEY_REQUEST_STATUS.COMPLETED) {
      continue;
    }

    if (request.action === KEY_REQUEST_ACTION.REVOKE) {
      if (request.lockId) {
        keys.delete(request.lockId);
      } else {
        keys.clear();
      }
      continue;
    }

    if (request.action !== KEY_REQUEST_ACTION.GRANT) {
      continue;
    }

    if (request.scope === KEY_REQUEST_SCOPE.SINGLE_LOCK && request.lockId) {
      addLocks([request.lockId], request);
    } else if (
      request.scope === KEY_REQUEST_SCOPE.ALL_LOCKS_FOR_RESIDENT &&
      request.residentId
    ) {
      addLocks(locksByResident.get(request.residentId) || [], request);
    } else if (request.scope === KEY_REQUEST_SCOPE.ALL_LOCKS) {
      addLocks(unlocLockIds, request);
    } else if (request.lockId) {
      addLocks([request.lockId], request);
    }
  }

  return [...keys.values()]
    .filter((key) => !key.expiresOn || key.expiresOn > now)
    .sort((left, right) => left.name.localeCompare(right.name, 'nb'));
}

const INACTIVE_UNLOC_STATES = new Set(['expired', 'revoked', 'cancelled', 'canceled', 'deleted']);

function lockNameForUnlocId(locks, lockId) {
  const match = locks.find(
    (lock) => lock.lockId === lockId || lock.externalId === lockId || lock.id === lockId
  );
  return match?.name || lockId || 'Ukjent lås';
}

export function parseUnlocKeysPayload(resultJson) {
  if (!resultJson) {
    return [];
  }

  if (typeof resultJson !== 'string') {
    return resultJson.keys || resultJson.value || [];
  }

  const trimmed = resultJson.trim();
  if (!trimmed) {
    return [];
  }

  try {
    const parsed = JSON.parse(trimmed);
    if (Array.isArray(parsed)) {
      return parsed;
    }
    return parsed.keys || parsed.value || [];
  } catch {
    return [];
  }
}

export function mapUnlocKeys(resultJson, locks = [], now = new Date()) {
  return parseUnlocKeysPayload(resultJson)
    .filter((key) => {
      const state = String(key.state || '').toLowerCase();
      if (INACTIVE_UNLOC_STATES.has(state)) {
        return false;
      }
      if (key.end && new Date(key.end) <= now) {
        return false;
      }
      return Boolean(key.lockId || key.id);
    })
    .map((key) => ({
      id: key.id || key.lockId,
      lockId: key.lockId,
      name: lockNameForUnlocId(locks, key.lockId),
      grantedOn: key.start || key.created || null,
      expiresOn: key.end || null,
      state: key.state || 'active'
    }))
    .sort((left, right) => left.name.localeCompare(right.name, 'nb'));
}

function isActiveUnlocKey(key, now) {
  const state = String(key.state || '').toLowerCase();
  if (INACTIVE_UNLOC_STATES.has(state)) {
    return false;
  }
  if (key.end && new Date(key.end) <= now) {
    return false;
  }
  return Boolean(key.lockId || key.id);
}

function extractUnlocHolder(key) {
  const appUser = key.appUser || {};
  const raw = appUser.id || key.appUserId || key.userId || key.phoneNumber || key.mobileNumber || '';
  const mobile = normalizeMobileNumber(String(raw)) || String(raw || '').trim();
  return {
    mobileNumber: mobile,
    unlocName: appUser.name || key.appUserName || ''
  };
}

function earlierTime(left, right) {
  if (!left) {
    return right;
  }
  if (!right) {
    return left;
  }
  return new Date(left) <= new Date(right) ? left : right;
}

function laterExpiry(left, right) {
  if (!left || !right) {
    return null;
  }
  return new Date(left) >= new Date(right) ? left : right;
}

/**
 * Group live Unloc keys for one lock by holder (mobile / Unloc app user).
 */
export function mapUnlocHoldersForLock(resultJson, phones = [], lockId = null, now = new Date()) {
  const phoneMap = phonesByMobile(phones);
  const holders = new Map();

  for (const key of parseUnlocKeysPayload(resultJson)) {
    if (!isActiveUnlocKey(key, now)) {
      continue;
    }
    if (lockId && key.lockId && key.lockId !== lockId) {
      continue;
    }

    const { mobileNumber, unlocName } = extractUnlocHolder(key);
    const holderId = mobileNumber || key.id || key.lockId;
    if (!holderId) {
      continue;
    }

    const phone = mobileNumber ? phoneMap.get(mobileNumber) : null;
    const grantedOn = key.start || key.created || null;
    const expiresOn = key.end || null;
    const existing = holders.get(holderId);

    if (!existing) {
      holders.set(holderId, {
        id: holderId,
        lockId: key.lockId || lockId,
        mobileNumber,
        holderName: phone?.name || unlocName || mobileNumber || 'Ukjent',
        registered: Boolean(phone),
        grantedOn,
        expiresOn,
        keyCount: 1
      });
      continue;
    }

    existing.keyCount += 1;
    existing.grantedOn = earlierTime(existing.grantedOn, grantedOn);
    existing.expiresOn = laterExpiry(existing.expiresOn, expiresOn);
    if (!existing.registered && phone) {
      existing.registered = true;
      existing.holderName = phone.name;
    }
  }

  return [...holders.values()].sort((left, right) =>
    left.holderName.localeCompare(right.holderName, 'nb')
  );
}

function phonesByMobile(phones) {
  const map = new Map();
  for (const phone of phones) {
    const mobile = normalizeMobileNumber(phone.mobileNumber);
    if (mobile) {
      map.set(mobile, phone);
    }
  }
  return map;
}

/**
 * Live Unloc keys across the project: who has access to which lock.
 */
export function mapUnlocAccessRows(resultJson, { locks = [], phones = [] } = {}, now = new Date()) {
  const phoneMap = phonesByMobile(phones);

  return parseUnlocKeysPayload(resultJson)
    .filter((key) => isActiveUnlocKey(key, now))
    .map((key) => {
      const { mobileNumber, unlocName } = extractUnlocHolder(key);
      const phone = mobileNumber ? phoneMap.get(mobileNumber) : null;
      return {
        id: key.id || `${mobileNumber}-${key.lockId}`,
        lockId: key.lockId,
        lockName: lockNameForUnlocId(locks, key.lockId),
        mobileNumber,
        holderName: phone?.name || unlocName || mobileNumber || 'Ukjent',
        registered: Boolean(phone),
        grantedOn: key.start || key.created || null,
        expiresOn: key.end || null
      };
    })
    .sort((left, right) => {
      const lockCmp = left.lockName.localeCompare(right.lockName, 'nb');
      if (lockCmp !== 0) {
        return lockCmp;
      }
      return left.holderName.localeCompare(right.holderName, 'nb');
    });
}

export function formatKeyTime(value) {
  if (!value) {
    return '—';
  }

  return new Date(value).toLocaleString('nb-NO', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
}
